import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { applySiteStyles, type SiteStylesData } from '../utils/siteStyles';

type SiteStyles = SiteStylesData;

const CACHE_KEY = 'site_styles_cache';

function readCachedStyles(): SiteStyles | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

export function useSiteStyles() {
  // Start from the cache so the first render already has saved values (e.g. decorations
  // at 0% opacity don't show for a frame before the effect runs).
  const [styles, setStyles] = useState<SiteStyles | null>(readCachedStyles);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cachedStyles = localStorage.getItem(CACHE_KEY);
    if (cachedStyles) {
      try {
        const parsed = JSON.parse(cachedStyles) as SiteStyles;
        setStyles(parsed);
        applySiteStyles(parsed);
      } catch {}
    }

    const fetchStyles = async () => {
      try {
        const { data, error } = await supabase
          .from('site_styles')
          // '*' rather than a column list, so code deployed ahead of a site_styles migration still loads
          .select('*')
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setStyles(data);
          applySiteStyles(data);
          localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        }
      } catch (error) {
        console.error('Error fetching site styles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStyles();
  }, []);

  return { styles, loading };
}
